
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import PhoneNumber from '@/common/components/UI/PhoneNumber/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// module components
import Geocoder from '@/modules/CreateOrderModule/components/Geocoder/index.vue'

// types
import { TemplateT } from '@/core/types/templates.types'
import { WarehouseT } from '@/core/types/warehouses.types'
import { PVZT } from '@/core/types/pvz.types'

// masks
import { PHONE_NUMBER_MASK_INTERNATIONAL } from '@/core/constants/masks.constants'

// validations
import useVuelidate from '@vuelidate/core'
import { CreateTemplateValidation } from '@/modules/CreateOrderModule/validations/CreateTemplate.validation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CityT } from '@/core/types/cities.types'

// realisations
import { cities } from '@/core/realisations/cities.realisation'
import { delivery } from '@/core/realisations/delivery.realisation'
import { warehouses } from '@/core/realisations/warehouses.realisation'
import { pvz } from '@/core/realisations/pvz.realisation'

// composable
import useHelpers from '@/common/composable/useHelpers'
import useCreateOrder from '@/modules/CreateOrderModule/composable/useCreateOrder'

export default defineComponent({
    components: {
        modal: Modal,
        'the-input': TheInput,
        'phone-number': PhoneNumber,
        'the-select': TheSelect,
        'the-textarea': TheTextarea,
        'the-button': TheButton,
        checkbox: Checkbox,
        //
        geocoder: Geocoder,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        isSender: {
            type: Boolean,
            default: false,
        },
        isReceiver: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<TemplateT>,
            default: null,
        },
        companyId: {
            type: Number,
            required: true,
        },
        senderId: {
            type: Number,
            default: null,
        },
        senderCityId: {
            type: Number,
            default: null,
        },
        receiverCityId: {
            type: Number,
            default: null,
        },
        loadBySenderCityId: {
            type: Boolean,
            default: false,
        },
        cityId: {
            type: Number,
            default: null,
        },
        isExpress: {
            type: Boolean,
            default: false,
        },
        isFtl: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['create', 'update', 'cancel'],
    setup(props, { emit }) {
        const state = reactive({
            id: undefined as number | undefined,
            //
            title: null as string | null,
            full_name: null as string | null,
            phone: null as string | null,
            additional_phone: null as string | null,
            city_id: null as number | null,
            city: null as CityT | null,
            //
            full_address: null as string | null,
            latitude: null as number | null,
            longitude: null as number | null,
            //
            index: null as string | null,
            office: null as string | null,
            comment: null as string | null,
            street: null as string | null,
            house: null as string | null,
            //
            warehouse_id: null as number | null,
        })

        const pvz_state = reactive({
            distribution_center_id: null as number | null,
            pvz: null as PVZT | null,
            is_selected: false,
        })

        const state_form$ = useVuelidate(CreateTemplateValidation, state)

        const warehouse_state = reactive({
            warehouse: null as WarehouseT | null,
            is_setted: false,
            is_loading: false,
            is_failed: false,
        })

        const geocode_state = reactive({
            is_opened: false,
            address: null as string | null,
            street: null as string | null,
            latitude: null as number | null,
            longitude: null as number | null,
            house: null as number | null,
        })

        const data_state = reactive({
            cities: new DataState<CityT>({}),
            pvz: new DataState<PVZT>({}),
        })

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                if (props.cityId && props.isExpress) {
                    const data = await delivery.getCities(page, search, 9999)
                    const city = data.data.find((city) => city.id === props.cityId)

                    if (city) {
                        data_state.cities.list = [city]
                    } else {
                        data_state.cities.list = []
                    }

                    data_state.cities.pagination.total_pages = 1
                } else {
                    const data = await cities.getAllList(page, search, 9999, undefined, 1)
                    data_state.cities.list = data.data
                    data_state.cities.pagination.total_pages = data.meta.last_page
                }

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadCitiesBySenderCityId = async () => {
            if (!props.senderCityId) return
            data_state.cities.pagination.current_page = 1
            data_state.cities.pagination.total_pages = 1

            try {
                data_state.cities.is_loading = true

                const response = await cities.getCitiesBySender(props.senderCityId)
                data_state.cities.list = response.data

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadMoreCities = async (page: number) => {
            if (page > data_state.cities.pagination.total_pages) return
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(page, undefined, undefined, undefined, 1)
                data_state.cities.list = data_state.cities.list.concat(response.data)

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const searchCity = (search_query: string) => {
            // data_state.cities.pagination.current_page
            loadCities(1, search_query)
        }

        const unshiftFindedCity = async (city: CityT) => {
            await loadCities(data_state.cities.pagination.current_page)
            data_state.cities.list.unshift(city)
        }

        const setWarehouse = () => {
            if (!warehouse_state.warehouse) {
                return
            }

            state.warehouse_id = warehouse_state.warehouse.id

            state.full_address = warehouse_state.warehouse.full_address
            state.latitude = Number(warehouse_state.warehouse.latitude)
            state.longitude = Number(warehouse_state.warehouse.longitude)
            state.index = warehouse_state.warehouse.index
            state.office = warehouse_state.warehouse.office
            state.house = warehouse_state.warehouse.house

            // clear selected PVZ input
            pvz_state.distribution_center_id = null
            pvz_state.is_selected = false
        }

        const resetWarehouse = () => {
            state.warehouse_id = null
            //
            state.full_address = null
            state.latitude = null
            state.longitude = null
            //
            state.index = null
            state.office = null
            state.house = null
        }

        const loadWarehouse = async (city_id: number) => {
            try {
                warehouse_state.is_loading = true

                const response = await warehouses.getWarehouse(city_id)
                warehouse_state.warehouse = response.data

                if (warehouse_state.is_setted) {
                    setWarehouse()
                }

                warehouse_state.is_failed = false
            } catch (error) {
                console.error(error)
                warehouse_state.is_failed = true

                resetWarehouse()
            } finally {
                warehouse_state.is_loading = false
            }
        }

        const toggleWarehouse = () => {
            if (warehouse_state.is_setted) {
                setWarehouse()
            } else {
                resetWarehouse()
            }
        }

        const onPVZSelect = () => {
            resetWarehouse()

            const { findObjectByValue } = useHelpers()
            const found_pvz = findObjectByValue(
                'id',
                pvz_state.distribution_center_id,
                data_state.pvz.list
            )

            if (!found_pvz) return
            pvz_state.pvz = { ...found_pvz }

            state.full_address = found_pvz.full_address
            state.latitude = Number(found_pvz.latitude)
            state.longitude = Number(found_pvz.longitude)

            pvz_state.is_selected = true
        }

        const onCitySelect = async () => {
            pvz_state.distribution_center_id = null
            pvz_state.is_selected = false

            if (!state.city_id) return

            const { findObjectByValue } = useHelpers()
            const finded_city = findObjectByValue('id', state.city_id, data_state.cities.list)

            if (!finded_city) return
            state.city = { ...finded_city }

            // resetting address on city change
            state.full_address = null
            state.latitude = null
            state.longitude = null

            loadWarehouse(state.city_id)
            if (props.isReceiver) {
                await loadPVZ(1)

                if (props.initialValue && props.initialValue.distribution_center_id) {
                    pvz_state.distribution_center_id = props.initialValue.distribution_center_id
                    onPVZSelect()
                }
            }
        }

        const refreshCities = () => {
            if (props.isSender) loadCities(data_state.cities.pagination.current_page)

            if (props.isReceiver) {
                if (props.loadBySenderCityId) {
                    loadCitiesBySenderCityId()
                } else {
                    loadCities(data_state.cities.pagination.current_page)
                }
            }
        }

        const modalTitle = computed(() => {
            if (geocode_state.is_opened) {
                if (props.isSender) return 'Укажите адрес отправного пункта'
                if (props.isReceiver) return 'Укажите адрес получателя'
            }

            if (props.isCreate) {
                if (props.isSender) return 'Добавление отправного пункта'
                if (props.isReceiver) return 'Добавление получателя'
            }

            if (props.isUpdate) {
                if (props.isSender) return 'Редактирование отправного пункта'
                if (props.isReceiver) return 'Редактирование получателя'
            }

            if (props.isDelete) {
                if (props.isSender) return 'Удаление отправного пункта'
                if (props.isReceiver) return 'Удаление получателя'
            }

            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (geocode_state.is_opened) return 'Подтвердить'
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        const { templatePayload } = useCreateOrder()

        const emitCreate = () => {
            const payload = templatePayload(
                state,
                props.companyId,
                props.senderId,
                pvz_state.distribution_center_id || undefined
            )
            emit('create', { ...payload })
        }

        const emitUpdate = () => {
            const payload = templatePayload(
                state,
                props.companyId,
                props.senderId,
                pvz_state.distribution_center_id || undefined
            )
            emit('update', { ...payload })
        }

        const emitManager = () => {
            if (geocode_state.is_opened) {
                if (!geocode_state.address || !geocode_state.latitude || !geocode_state.longitude) {
                    return
                }

                state.full_address = geocode_state.address
                state.street = geocode_state.street
                state.latitude = geocode_state.latitude
                state.longitude = geocode_state.longitude

                geocode_state.address = null
                geocode_state.street = null
                geocode_state.is_opened = false

                return
            }

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
        }

        const onStartGeocode = () => {
            state_form$.value.$reset()

            state_form$.value.city_id.$touch()
            if (state_form$.value.city_id.$error) return

            geocode_state.is_opened = true

            geocode_state.address = state.full_address
            geocode_state.street = state.street
            geocode_state.latitude = state.latitude
            geocode_state.longitude = state.longitude
        }

        const onCancelGeocode = () => {
            geocode_state.is_opened = false
            geocode_state.address = null
            geocode_state.street = null
            geocode_state.latitude = null
            geocode_state.longitude = null
        }

        const onSetAddress = ({
            address,
            street,
            latitude,
            longitude,
        }: {
            address: string
            street: string | null
            latitude: number
            longitude: number
        }) => {
            geocode_state.address = address
            geocode_state.street = street
            geocode_state.latitude = latitude
            geocode_state.longitude = longitude
            state.house = address.split(', ')[1]

            // clear selected PVZ
            pvz_state.distribution_center_id = null
            pvz_state.is_selected = false
        }

        const emitCancel = () => {
            if (geocode_state.is_opened) {
                onCancelGeocode()
                return
            }

            emit('cancel')
        }

        const loadPVZ = async (page: number) => {
            data_state.pvz.pagination.current_page = page
            const params = {
                page,
                cityId: state.city?.id.toString(),
            }

            try {
                data_state.pvz.is_loading = true
                const response = await pvz.getList(params)

                data_state.pvz.list = response.data
                data_state.pvz.pagination.total_pages = response.meta.last_page

                data_state.pvz.is_failed = false
                data_state.pvz.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.pvz.failure.catchError(error)

                if (!data_state.pvz.failure.error_state.is_cancel) {
                    data_state.pvz.is_failed = true
                }
            } finally {
                if (!data_state.pvz.failure.error_state.is_cancel) {
                    data_state.pvz.is_loading = false
                }
            }
        }

        const refreshPVZ = () => {
            loadPVZ(data_state.pvz.pagination.current_page)
        }

        const loadMorePVZ = async (page: number) => {
            if (page > data_state.pvz.pagination.total_pages) return
            data_state.pvz.pagination.current_page = page

            try {
                data_state.pvz.is_loading = true

                const response = await pvz.getList({ page })
                data_state.pvz.list = data_state.pvz.list.concat(response.data)

                data_state.pvz.pagination.total_pages = response.meta.last_page

                data_state.pvz.is_failed = false
                data_state.pvz.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.pvz.failure.catchError(error)

                if (!data_state.pvz.failure.error_state.is_cancel) {
                    data_state.pvz.is_failed = true
                }
            } finally {
                if (!data_state.pvz.failure.error_state.is_cancel) {
                    data_state.pvz.is_loading = false
                }
            }
        }

        const isAddressDisabled = computed(() => {
            return (
                Boolean(state.warehouse_id) ||
                (!state.warehouse_id && !state.city) ||
                (props.initialValue &&
                    props.initialValue.status &&
                    props.isReceiver &&
                    [210, 211].includes(props.initialValue.status))
            )
        })

        onBeforeMount(async () => {
            if (props.initialValue) {
                state.id = props.initialValue.id
                state.title = props.initialValue.title
                state.full_name = props.initialValue.full_name
                state.comment = props.initialValue.comment
                state.street = props.initialValue.street
                state.house = props.initialValue.house
                state.phone = props.initialValue.phone
                state.additional_phone = props.initialValue.additional_phone
                state.full_address = props.initialValue.full_address
                state.latitude = Number(props.initialValue.latitude)
                state.longitude = Number(props.initialValue.longitude)
                state.index = props.initialValue.index
                state.office = props.initialValue.office
                state.warehouse_id = props.initialValue.warehouse_id
                state.city_id = props.initialValue.city_id

                await loadWarehouse(state.city_id)

                if (state.warehouse_id) {
                    warehouse_state.is_setted = true
                }

                if (props.isSender) {
                    await loadCities(1)
                    // await loadPVZ(1)
                } else if (props.isReceiver) {
                    await loadPVZ(1)

                    if (props.initialValue && props.initialValue.distribution_center_id) {
                        pvz_state.distribution_center_id = props.initialValue.distribution_center_id
                        onPVZSelect()
                    }

                    if (props.loadBySenderCityId) {
                        await loadCitiesBySenderCityId()
                    } else {
                        await loadCities(1)
                    }
                }

                const { findObjectByValue } = useHelpers()
                const finded_city = findObjectByValue('id', state.city_id, data_state.cities.list)
                if (!finded_city) return

                state.city = { ...finded_city }
                return
            }

            if (props.isSender) {
                await loadCities(1)

                if (props.senderCityId) {
                    state.city_id = props.senderCityId
                    onCitySelect()
                }

                // await loadPVZ(1)
            } else if (props.isReceiver) {
                if (props.loadBySenderCityId) {
                    await loadCitiesBySenderCityId()
                } else {
                    await loadCities(1)
                    if (props.receiverCityId) {
                        state.city_id = props.receiverCityId
                        onCitySelect()
                    }
                }
            }
        })

        return {
            PHONE_NUMBER_MASK_INTERNATIONAL,
            modalTitle,
            modalConfirmButtonText,
            state,
            state_form$,
            data_state,
            //
            warehouse_state,
            pvz_state,
            //
            loadCities,
            loadMoreCities,
            searchCity,
            unshiftFindedCity,
            onCitySelect,
            refreshCities,
            //
            emitManager,
            emitCancel,
            //
            geocode_state,
            onStartGeocode,
            onCancelGeocode,
            onSetAddress,
            //
            toggleWarehouse,
            //
            refreshPVZ,
            loadMorePVZ,
            onPVZSelect,
            isAddressDisabled,
        }
    },
})
